import { createMemo, For, Show } from 'solid-js'
import Button from '~/components/Button'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import Select from '~/components/Select'
import Switch from '~/components/Switch'
import Textarea from '~/components/Textarea'
import type { NodeTypeDTOType, WorkflowFieldType, WorkflowNodeType } from '~/types/workflow'

export interface NodeInspectorPropsType {
  node: WorkflowNodeType | null
  /** Definición del tipo de nodo seleccionado. */
  def: NodeTypeDTOType | null
  /** Cambia una config del nodo. Se ignora si no hay nodo seleccionado. */
  onConfig: (key: string, value: unknown) => void
  onName: (name: string) => void
  onDuplicate: (id: string) => void
  onRemove: (id: string) => void
  onToggleDisable?: (id: string) => void
  /** Variables disponibles para insertar con un clic. */
  availableVars?: string[]
}

const FIELD_CLASS = 'space-y-1'
const LABEL_CLASS = 'text-xs font-medium text-foreground-muted'
const HELP_CLASS = 'text-[11px] leading-snug text-foreground-subtle'

/**
 * Panel de configuración del nodo seleccionado. Misma fuente de verdad que el
 * backend (`def.fields`): un campo por tipo. Es lo que se reutiliza en el
 * sidebar de escritorio, en el drawer y en la hoja inferior de móvil.
 */
export function NodeInspector(props: NodeInspectorPropsType) {
  const node = createMemo(() => props.node)
  const def = createMemo(() => props.def)

  const configuredKeys = createMemo(() => Object.keys(node()?.config ?? {}))

  const fieldRowClass = (width: WorkflowFieldType['width']) => {
    if (width === 'half') return 'sm:col-span-1'
    if (width === 'third') return 'sm:col-span-1'
    return 'sm:col-span-2'
  }

  const renderField = (field: {
    key: string
    label: string
    type: string
    required?: boolean
    default?: unknown
    help?: string
    placeholder?: string
    options?: Array<{ value: string; title: string }>
    rows?: number
    secret?: boolean
    width?: WorkflowFieldType['width']
  }) => {
    const n = node()
    if (!n) return null
    const val: unknown = n.config[field.key]

    return (
      <div class={fieldRowClass(field.width)}>
        <Show when={field.type === 'select'}>
          <Select
            label={field.label}
            value={String(val ?? field.default ?? '')}
            options={(field.options ?? []).map(o => ({ value: o.value, title: o.title }))}
            onChange={v => props.onConfig(field.key, v)}
          />
        </Show>

        <Show when={field.type === 'boolean'}>
          <Switch label={field.label} checked={!!val} onChange={v => props.onConfig(field.key, v)} />
        </Show>

        <Show when={field.type === 'textarea' || field.type === 'json' || field.type === 'code'}>
          <div class={FIELD_CLASS}>
            <label class={LABEL_CLASS}>{field.label}</label>
            <Textarea
              value={typeof val === 'string' ? val : val ? JSON.stringify(val, null, 2) : ''}
              onInput={e => props.onConfig(field.key, (e.currentTarget as HTMLTextAreaElement).value)}
              rows={field.rows ?? 3}
              placeholder={field.placeholder}
              aria-label={field.label}
            />
            <Show when={field.help}>
              <p class={HELP_CLASS}>{field.help}</p>
            </Show>
          </div>
        </Show>

        <Show when={field.type === 'number' || field.type === 'slider'}>
          <Input
            label={field.label}
            type="number"
            value={String(val ?? '')}
            onInput={e => props.onConfig(field.key, (e.currentTarget as HTMLInputElement).value === '' ? undefined : Number((e.currentTarget as HTMLInputElement).value))}
            required={field.required}
            aria-label={field.label}
          />
        </Show>

        <Show when={field.type === 'multiselect' || field.type === 'tags'}>
          <Input
            label={field.label}
            value={Array.isArray(val) ? val.join(', ') : String(val ?? '')}
            placeholder={field.placeholder ?? 'a, b, c'}
            onInput={e => {
              const raw = (e.currentTarget as HTMLInputElement).value
              props.onConfig(
                field.key,
                raw
                  .split(',')
                  .map(s => s.trim())
                  .filter(Boolean)
              )
            }}
            required={field.required}
            aria-label={field.label}
          />
        </Show>

        <Show
          when={
            field.type !== 'select' &&
            field.type !== 'boolean' &&
            field.type !== 'textarea' &&
            field.type !== 'json' &&
            field.type !== 'code' &&
            field.type !== 'number' &&
            field.type !== 'slider' &&
            field.type !== 'multiselect' &&
            field.type !== 'tags'
          }
        >
          <Input
            label={field.label}
            type={field.secret ? 'password' : 'text'}
            value={String(val ?? '')}
            placeholder={field.placeholder ?? ''}
            onInput={e => props.onConfig(field.key, (e.currentTarget as HTMLInputElement).value)}
            required={field.required}
            aria-label={field.label}
          />
        </Show>
      </div>
    )
  }

  return (
    <Show
      when={node() && def()}
      fallback={
        <div class="py-8 text-center" data-testid="node-inspector-fallback">
          <Icon icon="material-symbols-light:info-outline" size={32} class="mx-auto text-foreground-subtle" />
          <p class="mt-2 text-sm text-foreground-subtle">Selecciona un nodo en el canvas para configurarlo.</p>
        </div>
      }
    >
      {(() => {
        const n = node()!
        const d = def()!
        return (
          <div class="space-y-4">
            <Input label="Nombre" value={n.name} onInput={e => props.onName((e.currentTarget as HTMLInputElement).value)} aria-label="Nombre" />

            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">{d.fields.map(renderField)}</div>

            {/* Errores de campos obligatorios */}
            <Show when={d.fields.filter(f => f.required).length > 0}>
              <p class="text-[11px] text-foreground-subtle">{configuredKeys().length} campo(s) configurado(s). Los campos obligatorios aparecen marcados.</p>
            </Show>

            <div class="flex flex-wrap gap-2 border-t border-border pt-3">
              <Button size="sm" variant="soft" color="warning" icon="material-symbols-light:content-copy" onClick={() => props.onDuplicate(n.id)}>
                Duplicar
              </Button>
              <Show when={props.onToggleDisable}>
                <Button size="sm" variant="soft" color="secondary" icon="material-symbols-light:visibility-off-outline" onClick={() => props.onToggleDisable!(n.id)}>
                  {n.disabled ? 'Habilitar' : 'Deshabilitar'}
                </Button>
              </Show>
              <Button size="sm" variant="soft" color="danger" icon="material-symbols-light:delete-outline" onClick={() => props.onRemove(n.id)}>
                Eliminar
              </Button>
            </div>

            {/* Variables disponibles */}
            <Show when={props.availableVars?.length}>
              <div class="border-t border-border pt-3">
                <p class="text-xs font-semibold text-foreground">Variables disponibles</p>
                <p class="text-[11px] text-foreground-subtle">Clic para copiar. Se resuelven en tiempo de ejecución.</p>
                <div class="mt-2 space-y-1">
                  <For each={props.availableVars}>
                    {v => (
                      <Button size="sm" variant="soft" color="default" fullWidth class="!justify-start font-mono !text-[11px]" tooltip={`Copiar ${v}`} onClick={() => navigator.clipboard.writeText(v)}>
                        <span class="truncate">{v}</span>
                      </Button>
                    )}
                  </For>
                </div>
              </div>
            </Show>

            {/* Metadatos */}
            <div class="border-t border-border pt-3">
              <p class="text-xs font-semibold text-foreground">Nodo</p>
              <dl class="mt-1 space-y-0.5 text-xs text-foreground-subtle">
                <div class="flex justify-between gap-2">
                  <dt>ID</dt>
                  <dd class="truncate font-mono">{n.id}</dd>
                </div>
                <div class="flex justify-between gap-2">
                  <dt>Tipo</dt>
                  <dd class="truncate font-mono">{n.type}</dd>
                </div>
                <Show when={n.disabled}>
                  <div class="flex justify-between gap-2">
                    <dt>Estado</dt>
                    <dd class="text-warning">Deshabilitado</dd>
                  </div>
                </Show>
              </dl>
            </div>
          </div>
        )
      })()}
    </Show>
  )
}
